import { useCallback, useEffect, useReducer } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getActiveSlides, getActiveSlidesForTotalsOnly } from '../config/slides'
import type { WrappedData, WrappedTotalsData } from '../types/wrapped'
import {
  wrappedStoryStateReducer,
  WrappedStoryActions,
  INITIAL_WRAPPED_STORY_STATE,
  WrappedStoryState,
  WrappedStoryDispatchAction,
} from 'contexts/Wrapped'
import { getMyWrapped, getSharedWrapped, getWrappedTotals } from 'api/wrapped/wrapped'
import { getWindowMockData } from '../devMocks'

interface UseWrappedDataOptions {
  uuid?: string
}

interface UseWrappedDataResult {
  isLoading: boolean
  hasError: boolean
  error: Error | null
  data: WrappedData | undefined
  totalsData: WrappedTotalsData | undefined
  state: WrappedStoryState
  dispatch: React.Dispatch<WrappedStoryDispatchAction>
  handleRetry: () => void
}

function useWrappedData({ uuid }: UseWrappedDataOptions): UseWrappedDataResult {
  const [state, dispatch] = useReducer(
    wrappedStoryStateReducer,
    INITIAL_WRAPPED_STORY_STATE,
  )

  // Fetch totals data (always needed for fallbacks)
  const {
    data: totalsData,
  } = useQuery({
    queryKey: ['wrapped-totals'],
    queryFn: getWrappedTotals,
    staleTime: 5 * 60 * 1000,
  })

  // Fetch user-specific wrapped data
  const {
    data,
    isLoading: isUserDataLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['wrapped', uuid ?? 'me'],
    queryFn: async() => {
      // Check for mock data first (development/QA only)
      const mockData = getWindowMockData()
      if (mockData) return mockData

      return uuid ? getSharedWrapped(uuid) : getMyWrapped()
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 404 errors - user has no wrapped data
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return false
      }
      return failureCount < 3
    },
  })

  // Check if the error is a 404 (user has no wrapped data)
  const is404Error = isError && error && typeof error === 'object' && 'status' in error && error.status === 404

  // Set totals data in context when available
  useEffect(() => {
    if (totalsData) {
      dispatch({
        type: WrappedStoryActions.setTotalsData,
        totalsData,
      })
    }
  }, [totalsData])

  // Set user data in context when available
  useEffect(() => {
    if (data) {
      const slides = getActiveSlides(data)
      dispatch({
        type: WrappedStoryActions.setData,
        data,
        slides,
        isTotalsOnlyMode: false,
      })
    }
  }, [data])

  // Handle 404 case: use totals-only mode
  useEffect(() => {
    if (is404Error && totalsData) {
      const slides = getActiveSlidesForTotalsOnly(totalsData)
      // Create a minimal WrappedData from totals for compatibility
      const totalsAsWrappedData: WrappedData = {
        personality: {
          type: 'the-curious-explorer', // Default personality for totals-only mode
          percentile: 0,
        },
        stats: {
          totalNights: totalsData.stats.totalNights,
          totalDistanceKm: totalsData.stats.totalDistanceKm,
          totalSavings: totalsData.stats.totalSavings,
          currency: totalsData.stats.currency,
          citiesVisited: [],
          countriesVisited: 0,
          offersViewed: totalsData.stats.offersViewed,
          averageOffersViewed: totalsData.stats.averageOffersViewed,
          destinationsSearched: totalsData.stats.destinationsSearched,
        },
        topDestinations: totalsData.topDestinations,
        topOffer: totalsData.topOffer,
        generatedAt: totalsData.generatedAt,
      }
      dispatch({
        type: WrappedStoryActions.setData,
        data: totalsAsWrappedData,
        slides,
        isTotalsOnlyMode: true,
      })
    }
  }, [is404Error, totalsData])

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  // Still loading if user data is loading and we don't have totals to fall back to
  const isLoading = isUserDataLoading && !is404Error

  return {
    isLoading,
    hasError: isError && !is404Error, // 404 is not an error - we use totals-only mode
    error: is404Error ? null : (error as Error | null),
    data,
    totalsData,
    state,
    dispatch,
    handleRetry,
  }
}

export default useWrappedData
